import React, { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getCountriesAPI, addCountryAPI, updateCountryAPI, deleteCountryAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import AddCountryModal from "./components/AddCountryModal";
import "./Country.css";

const Country = () => {
  const [countries, setCountries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryName, setCountryName] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);

  const PAGE_LIMIT = 10;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCountriesAPI({
        page,
        limit: PAGE_LIMIT,
        search: normalizedSearch || "",
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        const currentPageFromApi = response?.pagination?.current_page || page;
        setCountries(list);
        setTotalPages(totalPagesFromApi);
        if (currentPageFromApi !== page) setPage(currentPageFromApi);
      } else {
        setError(response?.message || 'Failed to fetch countries');
      }
    } catch (err) {
      console.log('Fetch error:', err);
      setError('Failed to fetch countries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const totalEntries = countries.length;
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_LIMIT;
  const endIdx = startIdx + countries.length;

  const handleEdit = (country) => {
    setSelectedCountry(country);
    setCountryName(country.name);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setSelectedCountry(null);
    setCountryName("");
    setIsEditMode(false);
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCountryName("");
    setIsEditMode(false);
    setSelectedCountry(null);
  };

  const handleDeleteClick = (country) => {
    setCountryToDelete(country);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCountryToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!countryToDelete?._id) {
        showToast("Invalid country data", "error");
        return;
      }
      
      const reqData = { country_id: countryToDelete._id };
      const response = await deleteCountryAPI(reqData);
      
      if (response?.status === 200) {
        setShowDeleteModal(false);
        setCountryToDelete(null);
        showToast(`Country "${countryToDelete?.name}" deleted successfully!`, "success");
        await fetchCountries(); // Refresh the list
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleSave = async () => {
    if (!countryName.trim()) {
      showToast("Please enter a country name.", "error");
      return;
    }

    setSaveLoading(true);

    try {
      if (isEditMode && selectedCountry) {
        // Update existing country
        const reqData = {
          country_id: selectedCountry._id,
          name: countryName
        };
        const response = await updateCountryAPI(reqData);

        if (response?.status === 200) {
          await fetchCountries();
          handleCloseModal();
          showToast("Country updated successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      } else {
        // Add new country
        const reqData = { name: countryName };
        const response = await addCountryAPI(reqData);

        if (response?.status === 200) {
          await fetchCountries();
          handleCloseModal();
          showToast("Country added successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const getSortedData = () => {
    const sorted = [...countries].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    return sorted;
  };

  const sortedCountries = getSortedData();

  const getPageItems = () => {
    const items = [];
    const current = Math.max(1, Math.min(page, totalPages));
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    const showLeftDots = current > 4;
    const showRightDots = current < totalPages - 3;

    if (showLeftDots) items.push('dots-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i++) items.push(i);

    if (showRightDots) items.push('dots-right');

    items.push(totalPages);
    return items;
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="Country-layout">
        <Sidebar />
        <div className="Country-main-content">
          <div className="Country-header-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search by country name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search countries"
            />
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Country"
            >
              <span>Add Country</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading countries...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : totalEntries === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌍</div>
              <h3>No Countries Found</h3>
              <p>There are no countries in the system yet.</p>
              <p>Click "Add Country" to create the first one.</p>
            </div>
          ) : countries.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
              <h3>No results</h3>
              <p>We couldn't find any country matching "{searchTerm}".</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="Country-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("name")}
                      >
                        <div className="header-content">
                          Country Name
                          <span className="sort-icon">
                            {getSortIcon("name")}
                          </span>
                        </div>
                      </th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCountries.map((country, idx) => (
                      <tr key={country._id || `country-${idx}`}>
                        <td>{startIdx + idx + 1}</td>
                        <td>{country.name || ''}</td>
                        <td>
                          <div className="country-action-btn-row">
                            <button
                              type="button"
                              className="country-action-btn country-edit-btn"
                              title="Edit Country"
                              onClick={() => handleEdit(country)}
                              aria-label="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="country-action-btn country-delete-btn"
                              title="Delete Country"
                              onClick={() => handleDeleteClick(country)}
                              aria-label="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {countries.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {getPageItems().map((item, idx) => (
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={`p-${item}`}
                        className={page === item ? 'active' : ''}
                        onClick={() => handlePageChange(item)}
                        aria-current={page === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Add/Edit Modal */}
          <AddCountryModal
            showModal={showModal}
            isEditMode={isEditMode}
            countryName={countryName}
            setCountryName={setCountryName}
            saveLoading={saveLoading}
            onSave={handleSave}
            onClose={handleCloseModal}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete the Country "${countryToDelete?.name || ''}"?`}
            confirmText="Delete"
          />
        </div>
      </div>
    </>
  );
};

export default Country; 